import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, Loader2, Pencil } from 'lucide-react';
import { useClient, type ClientRecord } from '@/api/clients';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge, Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { calcAge, fmtDob, authRemainingHours, authExpiryWarning } from './clientHelpers';
import { AuthExpiryIndicator } from './AuthExpiryIndicator';
import { ClientOverviewTab } from './tabs/ClientOverviewTab';
import { ClientDemographicsTab } from './tabs/ClientDemographicsTab';
import { ClientBackgroundTab } from './tabs/ClientBackgroundTab';
import { ClientAuthTab } from './tabs/ClientAuthTab';
import { ClientEligibilitySection } from './ClientEligibilitySection';
import { ClientContactsTab } from './tabs/ClientContactsTab';
import { ClientStaffTab } from './tabs/ClientStaffTab';
import { ClientCustomFieldsTab } from './tabs/ClientCustomFieldsTab';
import { ClientHistoryTab } from './tabs/ClientHistoryTab';
import { ClientBehaviorsTab } from './tabs/ClientBehaviorsTab';
import { ClientProgramsTab } from './tabs/ClientProgramsTab';
import { ClientAvailabilityTab } from './tabs/ClientAvailabilityTab';
import { ClientPortalAccountsTab } from './tabs/ClientPortalAccountsTab';
import { StatusChangeDialog } from './StatusChangeDialog';

const TABS = [
    { id: 'overview',     label: 'Overview' },
    { id: 'demographics', label: 'Demographics' },
    { id: 'background',   label: 'Background' },
    { id: 'behaviors',    label: 'Behaviors' },
    { id: 'programs',     label: 'Programs' },
    { id: 'custom',       label: 'Custom Fields' },
    { id: 'auths',        label: 'Auth & Eligibility' },
    { id: 'contacts',     label: 'Contact Log' },
    { id: 'portal',       label: 'Portal Accounts' },
    { id: 'staff',        label: 'Staff' },
    { id: 'availability', label: 'Availability' },
    { id: 'history',      label: 'History' },
] as const;
type TabId = typeof TABS[number]['id'];

// Tabs the edit form has corresponding panels for. When switching from view
// to edit we preserve the current tab if it's in this set; otherwise we
// default the edit view to overview.
const EDIT_TABS: readonly TabId[] = ['overview', 'demographics', 'background', 'custom'];

function initials(c: ClientRecord) {
    return (c.first[0] || '') + (c.last[0] || '');
}

export function ClientDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { data, isLoading, isError, error } = useClient(id);
    const navigate = useNavigate();
    const location = useLocation();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_clients'));
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);

    const activeTab: TabId = useMemo(() => {
        const fromHash = location.hash.replace(/^#/, '') as TabId;
        return TABS.some((t) => t.id === fromHash) ? fromHash : 'overview';
    }, [location.hash]);

    useEffect(() => {
        if (!location.hash) {
            navigate({ hash: '#overview' }, { replace: true });
        }
    }, [location.hash, navigate]);

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading client…
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-6 max-w-3xl mx-auto">
                <Link to="/clients" className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text mb-3">
                    <ChevronLeft className="w-3 h-3" /> Back to clients
                </Link>
                <Card>
                    <p className="font-medium text-error mb-1">Couldn't load this client.</p>
                    <p className="text-xs text-text-subtle">
                        {extractErrorMessage(error, 'You may not have access, or the record may not exist.')}
                    </p>
                </Card>
            </div>
        );
    }

    if (!data) return null;

    const c = data;
    const age = calcAge(c.dob);
    const primaryAuth = findPrimaryAuthSummary(c);
    const primaryAuthRem = primaryAuth ? authRemainingHours(primaryAuth) : null;
    const authWarning = authExpiryWarning(c.status, primaryAuth);

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <Link to="/clients" className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text mb-3">
                <ChevronLeft className="w-3 h-3" /> Back to clients
            </Link>

            <Card className="mb-4">
                {/* flex-wrap so the badges + Edit button drop to a new line on
                    narrow widths instead of crushing the client name. */}
                <div className="flex flex-wrap items-start gap-4">
                    <div
                        aria-hidden
                        className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent text-[#111] text-base font-bold"
                    >
                        {initials(c)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display text-2xl font-bold text-text">
                            {c.first} {c.last}
                        </h1>
                        <p className="text-xs text-text-subtle mt-0.5">
                            {c.dx1 || '—'}
                            {c.dx2 && ` · ${c.dx2}`}
                            {age !== null && ` · Age ${age}`}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {primaryAuthRem !== null && (
                            <Badge variant="info" className="h-7 px-2.5">{primaryAuthRem} auth hrs remaining</Badge>
                        )}
                        {canEdit ? (
                            <button
                                type="button"
                                onClick={() => setStatusDialogOpen(true)}
                                aria-label="Change status"
                                title="Change status"
                                className="rounded-sm focus:outline-none focus:ring-2 focus:ring-accent"
                            >
                                <StatusBadge
                                    status={c.status}
                                    trailingIcon={<ChevronDown className="w-3 h-3 -mr-0.5 opacity-70" />}
                                    className="h-7 px-2.5 cursor-pointer ring-1 ring-transparent hover:ring-border hover:brightness-95"
                                />
                            </button>
                        ) : (
                            <StatusBadge status={c.status} className="h-7 px-2.5" />
                        )}
                        <AuthExpiryIndicator warning={authWarning} className="h-7 px-2" />
                        {canEdit && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    const targetHash = (EDIT_TABS as readonly string[]).includes(activeTab)
                                        ? `#${activeTab}`
                                        : '#overview';
                                    navigate(`/clients/${c.id}/edit${targetHash}`);
                                }}
                            >
                                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                            </Button>
                        )}
                    </div>
                </div>
            </Card>

            <div role="tablist" aria-label="Client detail sections" className="flex flex-wrap gap-1 mb-3 border-b border-border">
                {TABS.map((t) => (
                    <Link
                        key={t.id}
                        to={{ hash: `#${t.id}` }}
                        replace
                        role="tab"
                        aria-selected={activeTab === t.id}
                        className={cn(
                            'px-3 py-2 text-xs font-medium -mb-px border-b-2 transition-colors',
                            activeTab === t.id
                                ? 'border-accent text-text'
                                : 'border-transparent text-text-muted hover:text-text',
                        )}
                    >
                        {t.label}
                    </Link>
                ))}
            </div>

            <div role="tabpanel">
                {activeTab === 'overview'     && <ClientOverviewTab client={c} />}
                {activeTab === 'demographics' && <ClientDemographicsTab client={c} />}
                {activeTab === 'background'   && <ClientBackgroundTab client={c} />}
                {activeTab === 'behaviors'    && <ClientBehaviorsTab client={c} />}
                {activeTab === 'programs'     && <ClientProgramsTab client={c} />}
                {activeTab === 'auths'        && (
                    <>
                        <ClientAuthTab client={c} />
                        <ClientEligibilitySection client={c} />
                    </>
                )}
                {activeTab === 'contacts'     && <ClientContactsTab client={c} />}
                {activeTab === 'portal'       && <ClientPortalAccountsTab client={c} />}
                {activeTab === 'staff'        && <ClientStaffTab client={c} />}
                {activeTab === 'availability' && <ClientAvailabilityTab client={c} />}
                {activeTab === 'custom'       && <ClientCustomFieldsTab client={c} />}
                {activeTab === 'history'      && <ClientHistoryTab client={c} />}
            </div>

            {canEdit && (
                <StatusChangeDialog
                    client={c}
                    open={statusDialogOpen}
                    onOpenChange={setStatusDialogOpen}
                />
            )}
        </div>
    );
}

import type { ClientAuthSummary } from '@/api/clients';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
function findPrimaryAuthSummary(c: ClientRecord): ClientAuthSummary | null {
    if (!c.auths?.length) return null;
    const now  = Date.now();
    const soon = now + 30 * 86400000;
    function statusOf(end?: string, start?: string): ClientAuthSummary['status'] {
        if (!end) return 'Pending';
        const endT = Date.parse(`${end}T00:00:00`);
        if (Number.isNaN(endT)) return 'Pending';
        if (endT < now)  return 'Expired';
        if (endT < soon) return 'Expiring';
        if (start) {
            const startT = Date.parse(`${start}T00:00:00`);
            if (!Number.isNaN(startT) && startT > now) return 'Pending';
        }
        return 'Active';
    }
    const primary = c.auths.find((a) => {
        const s = statusOf(a.end, a.start);
        return s === 'Active' || s === 'Expiring';
    }) || c.auths[0];
    return {
        hours:  Number(primary.hours) || 0,
        used:   Number(primary.used)  || 0,
        end:    primary.end ?? null,
        status: statusOf(primary.end, primary.start),
    };
}

export { fmtDob };
